import { z } from "zod";

import UserApiBase from "../base/UserApiBase";

import { type OkResponse } from "@/schemas/api/ok";
import {
  type PaginatedRequest,
  type PaginatedResponse,
  createPaginatedResponseSchema,
} from "@/schemas/api/pagination";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  type UserProfilePreview,
  UserProfilePreviewSchema,
} from "@/schemas/user/preview";

export enum RequestType {
  PRIVATE_PHOTOS = "PRIVATE_PHOTOS",
}

export enum RequestState {
  INIT = "INIT",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface PermissionRequest {
  id: string;
  grantee: UserProfilePreview;
  type: RequestType;
  status: RequestState;
}

export interface PermissionAccess {
  status: RequestState | null;
}

export const PermissionAccessSchema = z.object({
  status: z
    .enum([RequestState.INIT, RequestState.APPROVED, RequestState.REJECTED])
    .nullable(),
});

export const PermissionRequestSchema = z.object({
  grantee: UserProfilePreviewSchema,
  id: z.string(),
  status: z.enum([
    RequestState.INIT,
    RequestState.APPROVED,
    RequestState.REJECTED,
  ]),
  type: z.enum([RequestType.PRIVATE_PHOTOS]),
});

export const PaginatedPermissionRequestSchema = createPaginatedResponseSchema(
  PermissionRequestSchema,
);

class PermissionService extends UserApiBase {
  public constructor(session: AppUserSession) {
    super("/permission", session);
  }

  public getHasAccess(
    type: RequestType,
    grantor: string,
  ): Promise<PermissionAccess> {
    return this.get(`/access/?type=${type}&grantor=${grantor}`);
  }

  public async getRequests(
    type: RequestType,
    state: RequestState,
    page: PaginatedRequest,
  ): Promise<PaginatedResponse<PermissionRequest>> {
    const paginateQueryStr = this.buildPaginateQueryStr(page);
    const response = await this.get(
      `/request/${paginateQueryStr}&type=${type}&status=${state}`,
    );
    const validatedResponse = PaginatedPermissionRequestSchema.parse(response);
    return validatedResponse;
  }

  public createRequest(
    type: RequestType,
    grantor: string,
  ): Promise<OkResponse> {
    return this.post(`/request/`, {
      grantor: grantor,
      permType: type,
    });
  }

  public updateRequest(
    id: string,
    newStatus: RequestState,
  ): Promise<OkResponse> {
    return this.patch(`/request/`, {
      permissionId: id,
      status: newStatus,
    });
  }
}

export default PermissionService;
